import { ProductInfoForKey, ProductKey } from "libs/product/src/lib/models/product.model";
import { PROFESSIONAL_REFINEMENT_SMALL_PACKAGE_PRICE, PROFESSIONAL_REFINEMENT_LARGE_PACKAGE_PRICE, PROFESSIONAL_REFINEMENT_DATA_PACKAGE_PRICE } from 'libs/sap/src/lib/services/sap.fixed-prices.constants';
import { ProfessionalTreatmentPackages } from "../models";

export class ProfessionalPackagesService {

	/**
	 * Checks whether the treatment is running depending on the state of the packages
	 * @param packages
	 * @returns true, if packages have already been ordered
	 */
	public static isTreatmentRunning( packages: ProfessionalTreatmentPackages ) : boolean {
		if(!packages){
			return false;
		}
		return packages.next > 0;
	}

	/**
	 * Checks whehter all regular packages are already ordered by the customer
	 * @param packages
	 * @returns true, if next package would be a (chargeable) correction package
	 */
	public static areRegularPackagesConsumed( packages: ProfessionalTreatmentPackages) : boolean {
		if(!packages || !packages.steps || !Array.isArray(packages.steps) || packages.steps.length === 0){
			return false;
		}
		return packages.next >= packages.steps.length;
	}

	/**
	 * Calculates the number and size of packages for a specific product based on the total steps
	 * @param totalBasePackageSteps
	 * @returns the calculated package structure
	 */
	public static generatePackageStructure(productKey: ProductKey, totalBasePackageSteps: number): ProfessionalTreatmentPackages {
		const productInfo = ProductInfoForKey[productKey];
		const defaultPackageSize = productInfo.basePackageSize;
		const maxPackageSteps = defaultPackageSize * productInfo.maxBasePackages;

		// Validate that the total steps do not exceed the maximum allowed
		if (totalBasePackageSteps > maxPackageSteps) {
		   throw new Error(`The total steps (${totalBasePackageSteps}) exceed the maximum allowed (${maxPackageSteps}) for ${productKey}`);
		}

		const steps: number[] = [];

		while (totalBasePackageSteps > 0) {
		  const stepsPerPackage = Math.min(defaultPackageSize, totalBasePackageSteps);
		  steps.push(stepsPerPackage);
		  totalBasePackageSteps -= stepsPerPackage;
		}

		// Get correction packages
		const freeCorrections = productInfo.corrections;
		return { steps, freeCorrections, next: 0 };
	  }

	  /**
	   * Returns the total count of all professional packages
	   * @param packages
	   * @returns count as number
	   */
	  public static getTotalPackageCount( packages: ProfessionalTreatmentPackages ): number {
			if (!packages){
				return 0;
			}
			const regularPackageCount = packages.steps.length + packages.freeCorrections.length;
			let chargeableRefinementsCount: number = 0;
			if( packages.addCorrections?.length >= 0 ){
				chargeableRefinementsCount = packages.addCorrections.length;
			}
			if( packages.chargeableRefinements?.length >= 0 ){
				chargeableRefinementsCount += packages.chargeableRefinements.length;
			}
			return regularPackageCount + chargeableRefinementsCount;
	  }

	  public static determineChargeableRefinementPackagePrice(isDataVariant: boolean, uj: number, lj: number): number {
		if (isDataVariant) {
			return PROFESSIONAL_REFINEMENT_DATA_PACKAGE_PRICE;
		} else if (uj <= 5 && lj <= 5) {
			return PROFESSIONAL_REFINEMENT_SMALL_PACKAGE_PRICE;
		} else {
			return PROFESSIONAL_REFINEMENT_LARGE_PACKAGE_PRICE;
		}
	}
}
